import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MultiComboBox,
  MultiComboBoxItem,
  Label,
  FlexBox,
  FlexBoxDirection,
  Button,
  RadioButton,
  Dialog,
  Input
} from "@ui5/webcomponents-react";
import UIModel from "../models/UIModel.json";

const EnvironmentSanitationForm = ({
  formData = {},
  onFormChange,
  handlePrevious,
  handleNext
}) => {
  const {
    waterSourceOptions = [],
    waterLocationOptions = [],
    waterAvailabilityOptions = ["Yes", "No", "Seasonal"],
    toiletFacilities = [],
    toiletSharingOptions = ["Yes", "No"],
    garbageDisposalOptions = []
  } = UIModel;

  const [localFormData, setLocalFormData] = useState({
    selectedWaterSource: formData.selectedWaterSource || [],
    selectedWaterLocation: formData.selectedWaterLocation || "",
    isWaterAvailable: formData.isWaterAvailable || "",
    selectedWaterSource2: formData.selectedWaterSource2 || [],
    selectedWaterLocation2: formData.selectedWaterLocation2 || "",
    isWaterAvailable2: formData.isWaterAvailable2 || "",
    selectedToiletFacility: formData.selectedToiletFacility || [],
    selectedToiletLocation: formData.selectedToiletLocation || "",
    toiletSharing: formData.toiletSharing || "",
    selectedGarbageDisposal: formData.selectedGarbageDisposal || []
  });

  const prevFormDataRef = useRef(localFormData);
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [dialogField, setDialogField] = useState(null);
  const [dialogFieldOptions, setDialogFieldOptions] = useState(null);

  const handleSelectionChange = (e, field, fieldOptions) => {
    // localFormData.selectedWaterSource = [];
    const selectedItems = Array.from(e.detail.items).map(item => item.text);
    const existingItems = localFormData[field];
    // Check if user selected or not
    const didUserSelected = selectedItems.length > existingItems.length
    // Extract the item that the user interacted with
    let itemInteracted;
    if (didUserSelected) { 
      itemInteracted = selectedItems.find(item => {
        const con1 = !existingItems.includes(item);
        const con2 = item === 'Others, specify' && hasOtherValues(existingItems, fieldOptions)
        return (con1 || con2)
      });
    } else {
      
      itemInteracted = existingItems.find(item => {
        // If there is a custom input, detect input as 'Others, specify'
        const con1 = !selectedItems.includes(fieldOptions.includes(item) ? item : 'Others, specify');
        return con1
      })
    }

    if (itemInteracted === "Others, specify") {
      if (didUserSelected) {
        setDialogField(field);
        setDialogFieldOptions(fieldOptions);
        setOpen(true);
      } else {
        handleMultiSelectChange(field, fieldOptions, e);
      }
    } else {
      handleMultiSelectChange(field, fieldOptions, e);
    }
  };

  const handleDialogSubmit = (field, fieldOptions) => {
    setOpen(false);
    handleMultiSelectChange(field, fieldOptions, customValue, true);
    setCustomValue("");
  };

  function hasOtherValues(sourceArray, targetArray) {
    const value = sourceArray.some(item => !targetArray.includes(item));
    return value;
  }


  // Only trigger onFormChange when there's an actual change
  useEffect(() => {
    if (
      JSON.stringify(prevFormDataRef.current) !==
      JSON.stringify(localFormData)
    ) {
      prevFormDataRef.current = localFormData;
      if (onFormChange) {
        onFormChange(localFormData);
      }
    }
  }, [localFormData]);

  const handleMultiSelectChange = useCallback((field, fieldOptions, e, isOthers = false) => {
    setLocalFormData((prev) => {
      const existingValues = Array.isArray(prev[field]) ? prev[field] : [];
      if (isOthers) {
        
        const newList = {
          ...prev,
          [field]: existingValues.includes(e)
            ? existingValues
            : [...existingValues, e],
        }
        return newList;
      } else {
        const customInput = existingValues.find(item => !fieldOptions.includes(item));
        const selectedItems = Array.from(e.detail.items).map((item) => item.text);
        const filteredItems = selectedItems.filter(item => item !== 'Others, specify' || customInput === undefined);
        const newList = {
          ...prev,
          [field]: customInput && selectedItems.includes('Others, specify') ? [...filteredItems, customInput] : filteredItems,
        }
        return newList;
      }
    });
  }, []);


  const handleSingleSelectChange = useCallback((field, e) => {
    const selectedValue = e.detail.items[0]?.text || "";
    setLocalFormData((prev) => ({
      ...prev,
      [field]: selectedValue
    }));
  }, []);

  const handleRadioChange = useCallback((field, value) => {
    setLocalFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "auto" }}>
      <h2>Step 7: Environment and Sanitation Information</h2>

      {/* E1 */}
      <h3>E1. Source of Water Used for General Use</h3>
      <FlexBox
        direction={FlexBoxDirection.Row}
        style={{ alignItems: "center", gap: "1rem", marginBottom: "1rem" }}
      >
        <Label style={{ flex: 1 }}>Water Source:</Label>
        <MultiComboBox style={{ flex: 2 }} onSelectionChange={(e) => handleSelectionChange(e, 'selectedWaterSource', waterSourceOptions)}>
          {waterSourceOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={
                localFormData.selectedWaterSource.includes(option) ||
                (option === 'Others, specify' && hasOtherValues(localFormData.selectedWaterSource, waterSourceOptions))
              }
            />
          ))}
        </MultiComboBox>

        <Dialog open={open} headerText="Enter custom value">
          <div style={{ padding: "1rem" }}>
            <Input
              value={customValue}
              onInput={(e) => setCustomValue(e.target.value)}
              placeholder="Type here..."
            />
            <div style={{ marginTop: "1rem", textAlign: "right" }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button design="Emphasized" onClick={() => handleDialogSubmit(dialogField, dialogFieldOptions)}>OK</Button>
            </div>
          </div>
        </Dialog>

        <Label style={{ flex: 1 }}>Where is the water source located?</Label>
        <MultiComboBox
          style={{ flex: 1 }}
          onSelectionChange={(e) =>
            handleSingleSelectChange("selectedWaterLocation", e)
          }
        >
          {waterLocationOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={localFormData.selectedWaterLocation === option}
            />
          ))}
        </MultiComboBox>
      </FlexBox>

      <FlexBox direction={FlexBoxDirection.Column} style={{ marginBottom: "1rem" }}>
        <Label>Is water available at least 12 hours per day?</Label>
        <FlexBox style={{ gap: "1rem" }}>
          {waterAvailabilityOptions.map((option) => (
            <RadioButton
              key={option}
              text={option}
              checked={localFormData.isWaterAvailable === option}
              onChange={() => handleRadioChange("isWaterAvailable", option)}
            />
          ))}
        </FlexBox>
      </FlexBox>

      {/* E2 */}
      <h3>E2. Source of Water Used for Drinking and Cooking</h3>
      <FlexBox
        direction={FlexBoxDirection.Row}
        style={{ alignItems: "center", gap: "1rem", marginBottom: "1rem" }}
      >
        <Label style={{ flex: 1 }}>Water Source:</Label>
        <MultiComboBox
          style={{ flex: 2 }}
          onSelectionChange={(e) => handleSelectionChange(e, 'selectedWaterSource2', waterSourceOptions)}
        >
          {waterSourceOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={
                localFormData.selectedWaterSource2.includes(option) ||
                (option === 'Others, specify' && hasOtherValues(localFormData.selectedWaterSource2, waterSourceOptions))
              }
            />
          ))}
        </MultiComboBox>

        <Label style={{ flex: 1 }}>Where is the water source located?</Label>
        <MultiComboBox
          style={{ flex: 1 }}
          onSelectionChange={(e) =>
            handleSingleSelectChange("selectedWaterLocation2", e)
          }
        >
          {waterLocationOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={localFormData.selectedWaterLocation2 === option}
            />
          ))}
        </MultiComboBox>
      </FlexBox>

      <FlexBox direction={FlexBoxDirection.Column} style={{ marginBottom: "1rem" }}>
        <Label>Is water available at least 12 hours per day?</Label>
        <FlexBox style={{ gap: "1rem" }}>
          {waterAvailabilityOptions.map((option) => (
            <RadioButton
              key={option}
              text={option}
              checked={localFormData.isWaterAvailable2 === option}
              onChange={() => handleRadioChange("isWaterAvailable2", option)}
            />
          ))}
        </FlexBox>
      </FlexBox>

      {/* E3 */}
      <h3>E3. Toilet Facilities</h3>
      <FlexBox
        direction={FlexBoxDirection.Row}
        style={{ alignItems: "center", gap: "1rem", marginBottom: "1rem" }}
      >
        <Label style={{ flex: 1 }}>Toilet Facility:</Label>
        <MultiComboBox
          style={{ flex: 2 }}
          onSelectionChange={(e) =>
            handleSelectionChange(e, 'selectedToiletFacility', toiletFacilities)
          }
        >
          {toiletFacilities.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={
                localFormData.selectedToiletFacility.includes(option) || 
                (option === 'Others, specify' && hasOtherValues(localFormData.selectedToiletFacility, toiletFacilities))
              }
            />
          ))}
        </MultiComboBox>

        <Label style={{ flex: 1 }}>Where is the toilet facility located?</Label>
        <MultiComboBox
          style={{ flex: 1 }}
          onSelectionChange={(e) =>
            handleSingleSelectChange("selectedToiletLocation", e)
          }
        >
          {waterLocationOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={localFormData.selectedToiletLocation === option}
            />
          ))}
        </MultiComboBox>
      </FlexBox>

      <FlexBox direction={FlexBoxDirection.Column} style={{ marginBottom: "1rem" }}>
        <Label>Is the toilet shared with other households?</Label>
        <FlexBox style={{ gap: "1rem" }}>
          {toiletSharingOptions.map((option) => (
            <RadioButton
              key={option}
              text={option}
              checked={localFormData.toiletSharing === option}
              onChange={() => handleRadioChange("toiletSharing", option)}
            />
          ))}
        </FlexBox>
      </FlexBox>

      {/* E4 */}
      <h3>E4. Garbage Disposal</h3>
      <FlexBox
        direction={FlexBoxDirection.Row}
        style={{ alignItems: "center", gap: "1rem", marginBottom: "2rem" }}
      >
        <Label style={{ flex: 1 }}>Garbage Disposal Methods:</Label>
        <MultiComboBox
          style={{ flex: 2 }}
          onSelectionChange={(e) =>
            handleSelectionChange(e, 'selectedGarbageDisposal', garbageDisposalOptions)
          }
        >
          {garbageDisposalOptions.map((option) => (
            <MultiComboBoxItem
              key={option}
              text={option}
              selected={
                localFormData.selectedGarbageDisposal.includes(option) || 
                (option === 'Others, specify' && hasOtherValues(localFormData.selectedGarbageDisposal, garbageDisposalOptions))
              }
            />
          ))}
        </MultiComboBox>
      </FlexBox>
    </div>
  );
};

export default EnvironmentSanitationForm;
